// Text diff renderers. Consume the engine's display-ready `render.segments`
// (and `render.rawRows`) and produce HTML matching the v2 template, plus the
// list of ribbon endpoint pairs the caller draws after layout.
import { esc } from './escape.js';
import { lineRange, lineLabel } from './line-range.js';

// Captions reflect the ACTUAL result, not a fixed script: an identical or
// move-free comparison must not claim a matched moved block.
const hasChange = (r) => (r.moved || 0) + (r.modified || 0) + (r.added || 0) + (r.removed || 0) > 0;
const moves = (r) => r.moved || 0;

function formattingCaption(r) {
  return `Final newline ${r.finalNewline === 'added' ? 'added' : 'removed'}. Text content is unchanged.`;
}

function capSxs(r) {
  if (!hasChange(r) && r.finalNewline) return formattingCaption(r);
  if (!hasChange(r)) return 'No differences — the two inputs are equivalent.';
  if (moves(r)) return 'A line-based tool shows this as separate deletions + additions. cruxdiff matched the moved block, so only the real changes are coloured.';
  return 'Changes are highlighted in place — additions in green, removals in red, edited words within a line picked out.';
}
function capUnified(r) {
  if (!hasChange(r) && r.finalNewline) return formattingCaption(r);
  if (!hasChange(r)) return 'No differences — the two inputs are equivalent.';
  if (moves(r)) return 'Semantic single-column view — the moved block is kept together instead of shown as delete-here / add-there.';
  return 'Semantic single-column view — additions, removals and in-line edits in reading order.';
}
function capRaw(r) {
  if (!hasChange(r) && r.finalNewline) return formattingCaption(r);
  if (!hasChange(r)) return 'Raw line view — no line-level differences.';
  if (moves(r)) return 'Raw line view — the move is invisible here (shown as delete-there + add-here). This is the fallback when semantic parsing isn’t possible.';
  return 'Raw line view — the literal line-based diff, the fallback when semantic parsing isn’t possible.';
}

export function renderText(result, view, meta = {}) {
  const out = view === 'unified' ? renderUnified(result) : view === 'raw' ? renderRaw(result) : renderSxs(result, meta);
  if (result.finalNewline && view !== 'sxs' && view != null) {
    out.html += `<div class="cap newline-note">${result.finalNewline === 'added' ? 'Original' : 'Changed'}: No newline at end of file.</div>`;
  }
  return out;
}

/* ---------- inline word highlights ---------- */
function inlineSide(inline, side) {
  // side: 'old' keeps context + removed words; 'new' keeps context + added words
  return inline
    .map((sp) => {
      if (sp.type === 'ctx') return esc(sp.text);
      if (side === 'old' && sp.type === 'old') return `<span class="hlOld">${esc(sp.text)}</span>`;
      if (side === 'new' && sp.type === 'new') return `<span class="hlNew">${esc(sp.text)}</span>`;
      return '';
    })
    .join('');
}
function inlineBoth(inline) {
  return inline
    .map((sp) =>
      sp.type === 'ctx'
        ? esc(sp.text)
        : sp.type === 'old'
          ? `<span class="edit-old">${esc(sp.text)}</span>`
          : `<span class="edit-new">${esc(sp.text)}</span>`,
    )
    .join('');
}

/* ---------- side-by-side ---------- */
// `at` stamps the row with the line numbers it represents. The change navigator
// resolves a stop's location to a row through these attributes, so no renderer
// needs to know that navigation exists (src/ui/change-nav.js owns that).
// A move HEADER is stamped with its own attribute, not `data-ln-*`: its first
// line number is also a real line's number, and one selector must not match
// both the marker and the line it labels.
const locAttrs = (at) => {
  const { l, r, moveL, moveR } = at || {};
  return `${l ? ` data-ln-l="${l}"` : ''}${r ? ` data-ln-r="${r}"` : ''}` +
    `${moveL ? ` data-move-l="${moveL}"` : ''}${moveR ? ` data-move-r="${moveR}"` : ''}`;
};

function sxsRow(lnL, cL, clsL, lnR, cR, clsR, idL = '', idR = '', at = null) {
  return `<div class="sxs-row"${locAttrs(at)}>` +
    `<span class="lnc">${lnL}</span><span class="c ${clsL}"${idL ? ` id="${idL}" tabindex="0" title="Hover or focus to show the move connection"` : ''}>${cL}</span>` +
    `<span class="gut"></span>` +
    `<span class="lnc">${lnR}</span><span class="c ${clsR}"${idR ? ` id="${idR}" tabindex="0" title="Hover or focus to show the move connection"` : ''}>${cR}</span>` +
    `</div>`;
}

function renderSxs(result, meta) {
  const leftLabel = esc(meta.leftLabel || 'Original');
  const rightLabel = esc(meta.rightLabel || 'Changed');
  const ribbons = [];
  let h = `<div class="sxs"><div class="sxs-hdr"><span></span><span class="h">${leftLabel}</span><span></span><span></span><span class="h">${rightLabel}</span></div>`;

  for (const s of result.render.segments) {
    switch (s.type) {
      case 'context':
        h += sxsRow(s.lnL, esc(s.lText) || ' ', 'ctx', s.lnR, esc(s.rText) || ' ', 'ctx', '', '', { l: s.lnL, r: s.lnR });
        break;
      case 'remove':
        h += sxsRow(s.lnL, esc(s.lText) || ' ', s.ignored ? 'ctx' : 'del', '', ' ', 'sp', '', '', { l: s.lnL });
        break;
      case 'add':
        h += sxsRow('', ' ', 'sp', s.lnR, esc(s.rText) || ' ', s.ignored ? 'ctx' : 'add', '', '', { r: s.lnR });
        break;
      case 'modify':
        h += sxsRow(s.lnL, inlineSide(s.inline, 'old') || ' ', 'del', s.lnR, inlineSide(s.inline, 'new') || ' ', 'add', '', '', { l: s.lnL, r: s.lnR });
        break;
      case 'move-origin':
        // Header (ribbon anchor) then the original moved lines, so the left
        // pane shows *what* moved (edited words struck through).
        h += sxsRow(
          `${lineRange(s.fromStart, s.fromEnd)}`,
          `⟡ moved to ${lineLabel(s.toStart, s.toEnd)} →`,
          'mvHeadL',
          '',
          ' ',
          'sp',
          `mo-${s.id}`,
          '',
          { moveL: s.fromStart },
        );
        for (const row of s.rows) {
          const content = row.inline ? inlineSide(row.inline, 'old') : row.edited ? `<span class="hlOld">${esc(row.lText)}</span>` : esc(row.lText);
          h += sxsRow(row.lnL, content || ' ', 'mvOrig', '', ' ', 'sp', '', '', { l: row.lnL });
        }
        break;
      case 'move-dest': {
        ribbons.push({ from: `#mo-${s.id}`, to: `#md-${s.id}`, ...(s.fromStart === s.fromEnd ? { interactive: true } : {}) });
        h += sxsRow(
          '',
          ' ',
          'sp',
          s.toStart,
          `MOVED · was ${lineLabel(s.fromStart, s.fromEnd)}${s.editedCount ? ` · ${s.editedCount} edited` : ''}`,
          'mvHead',
          '',
          `md-${s.id}`,
          { moveR: s.toStart },
        );
        for (const row of s.rows) {
          if (row.op === 'equal') h += sxsRow('', ' ', 'sp', row.lnR, esc(row.rText) || ' ', 'mvR', '', '', { r: row.lnR });
          else if (row.op === 'modify') h += sxsRow('', ' ', 'sp', row.lnR, inlineSide(row.inline, 'new') || ' ', 'mvR', '', '', { r: row.lnR });
          else if (row.op === 'ins') h += sxsRow('', ' ', 'sp', row.lnR, `<span class="hlNew">${esc(row.rText)}</span>`, 'mvR', '', '', { r: row.lnR });
          else h += sxsRow('', ' ', 'sp', '', `<span class="hlOld">${esc(row.lText)}</span>`, 'mvR');
        }
        break;
      }
      default:
        break;
    }
  }
  if (result.finalNewline) {
    const note = '<span class="newline-note">No newline at end of file</span>';
    h += sxsRow('', result.finalNewline === 'added' ? note : '', 'ctx', '', result.finalNewline === 'removed' ? note : '', 'ctx');
  }
  h += `<div class="cap">${capSxs(result)}</div></div>`;
  return { html: h, ribbons };
}

/* ---------- unified (semantic single column) ---------- */
const line = (n, t, cls = '', at = null) =>
  `<div class="code-line ${cls}"${locAttrs(at)}><span class="ln">${cls === 'del' ? '−' : cls === 'add' ? '+' : n}</span><span class="code">${t || ' '}</span></div>`;

function renderUnified(result) {
  let h = '';
  for (const s of result.render.segments) {
    switch (s.type) {
      case 'context':
        h += line(s.lnR, esc(s.rText), '', { l: s.lnL, r: s.lnR });
        break;
      case 'remove':
        h += line('', esc(s.lText), s.ignored ? '' : 'del', { l: s.lnL });
        break;
      case 'add':
        h += line('', esc(s.rText), s.ignored ? '' : 'add', { r: s.lnR });
        break;
      case 'modify':
        h += line('', inlineSide(s.inline, 'old'), 'del', { l: s.lnL }) + line('', inlineSide(s.inline, 'new'), 'add', { l: s.lnL, r: s.lnR });
        break;
      case 'move-origin': {
        let body = '';
        for (const row of s.rows) {
          body += line(row.lnL, row.inline ? inlineSide(row.inline, 'old') : row.edited ? `<span class="edit-old">${esc(row.lText)}</span>` : esc(row.lText), '', { l: row.lnL });
        }
        h += `<div class="moved-block" id="gho-${s.id}" data-move-l="${s.fromStart}"><div class="mb-head"><span class="mb-tag">MOVED</span> from here (${lineLabel(s.fromStart, s.fromEnd)}) → ${lineLabel(s.toStart, s.toEnd)}</div>${body}</div>`;
        break;
      }
      case 'move-dest': {
        let body = '';
        for (const row of s.rows) {
          if (row.op === 'equal') body += line(row.lnR, esc(row.rText), '', { r: row.lnR });
          else if (row.op === 'modify') body += line(row.lnR, inlineBoth(row.inline), '', { r: row.lnR });
          else if (row.op === 'ins') body += line(row.lnR, `<span class="edit-new">${esc(row.rText)}</span>`, '', { r: row.lnR });
          else body += line('', `<span class="edit-old">${esc(row.lText)}</span>`, '');
        }
        h += `<div class="moved-block" id="mvb-${s.id}" data-move-r="${s.toStart}"><div class="mb-head"><span class="mb-tag">MOVED</span> ${lineLabel(s.fromStart, s.fromEnd)} → ${lineRange(s.toStart, s.toEnd)}${s.editedCount ? ` · ${s.editedCount} edited inside` : ''}</div>${body}</div>`;
        break;
      }
      default:
        break;
    }
  }
  h += `<div class="cap" style="margin-left:16px">${capUnified(result)}</div>`;
  return { html: h, ribbons: [] };
}

/* ---------- raw (line-based literal) ---------- */
function renderRaw(result) {
  const h =
    result.render.rawRows.map((r) => line(r.ln, esc(r.text), r.cls, r.cls === 'del' ? { l: r.ln } : { r: r.ln })).join('') +
    `<div class="cap" style="margin-left:16px">${capRaw(result)}</div>`;
  return { html: h, ribbons: [] };
}
